import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import React, {FC} from 'react';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Modal} from 'semantic-ui-react';

import {urlPrefixed} from '../config';
import {
  useDataRetentionDurationQuery,
  useUndeleteRunsMutation,
} from '../generated/graphql';
import {
  PROJECT_PAGE_QUERY,
  ProjectData,
} from '../state/graphql/projectPageQuery';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../util/errors';
import {toMongo} from '../util/filters';
import * as urls from '../util/urls';

const DAY = 24 * 3600 * 1000;

export interface UndeleteRunsModalProps {
  open: boolean;
  project: ProjectData;
  onClose(): void;
}

export const UndeleteRunsModal: FC<UndeleteRunsModalProps> = ({
  open,
  project,
  onClose,
}) => {
  const history = useHistory();
  const {entityName, name: projectName} = project;
  const [undeleteRuns] = useUndeleteRunsMutation({
    context: {...propagateErrorsContext(), ...doNotRetryContext()},
    refetchQueries: [
      {
        query: PROJECT_PAGE_QUERY,
        variables: {entityName, projectName},
      },
    ],
    awaitRefetchQueries: true,
  });

  const dataRetentionDurationQuery = useDataRetentionDurationQuery({});

  // If data retention is disabled, default to 7 days
  // We don't want to undelete all runs ever, that is probably not the user's intent
  // and could be a very expensive operation
  const undeleteRunsLookbackMs =
    dataRetentionDurationQuery.data?.serverInfo?.dataRetentionDuration ||
    7 * DAY;

  const undeleteRunsLookbackDays = Math.ceil(undeleteRunsLookbackMs / DAY);
  const undeleteRunsLookbackStr = `${undeleteRunsLookbackDays} day${
    undeleteRunsLookbackDays === 1 ? '' : 's'
  }`;

  const handleUndeleteRuns = async () => {
    try {
      await undeleteRuns({
        variables: {
          filters: JSON.stringify(
            toMongo({
              op: '>=',
              key: {
                section: 'run',
                name: 'deletedAt',
              },
              value: new Date(Date.now() - undeleteRunsLookbackMs).toJSON(),
            })
          ),
          entityName: project.entity.name,
          projectName: project.name,
        },
      });

      onClose();

      history.push(
        urlPrefixed(
          urls.projectObjTab(
            project.entity.name,
            project.name,
            undefined,
            undefined,
            'table'
          )
        )
      );
    } catch (e) {
      console.error(extractErrorMessageFromApolloError(e));
      toast(`Error attempting to undelete runs for ${project.name}`);
    }
  };

  return (
    <Modal
      size="small"
      open={open}
      onClose={onClose}
      onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
      <Modal.Content>
        <p>
          You are about to undelete all runs in this project deleted in the last{' '}
          {undeleteRunsLookbackStr}.
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button className="wb-icon-button" size="tiny" onClick={onClose}>
          <LegacyWBIcon name="close" />
          Cancel
        </Button>
        <Button
          color="red"
          size="tiny"
          className="wb-icon-button"
          onClick={handleUndeleteRuns}>
          <LegacyWBIcon name="check" style={{color: 'white'}} /> Undelete all
          runs deleted in the last {undeleteRunsLookbackStr}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
